import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, Calendar, RotateCcw, RefreshCcw, Wrench, AlertTriangle } from 'lucide-react';
import { formatDate, getDaysRemaining } from './utils';

export const ActiveAssignmentsSection = ({ assignments, isAdmin, onReturn, onReplace }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-2xl font-heading font-bold">Outils Actuellement Affectés</h2>
      <Badge variant="default" className="text-base px-4 py-1">
        {assignments.length} actif{assignments.length > 1 ? 's' : ''}
      </Badge>
    </div>

    {assignments.length > 0 ? (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {assignments.map((assignment) => {
          const daysRemaining = getDaysRemaining(assignment.end_date);
          const isExpiring = daysRemaining !== null && daysRemaining <= 7;
          const hasPendingIssue = assignment.has_pending_issue;
          
          return (
            <Card key={assignment.id} data-testid={`active-assignment-${assignment.id}`}
              className={`hover:shadow-lg transition-shadow duration-200 
                ${hasPendingIssue ? 'bg-pink-50 border-pink-300 border-2' : ''}
                ${isExpiring && !hasPendingIssue ? 'border-destructive border-2' : ''}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    {assignment.tool?.photo ? (
                      <div className={`w-20 h-20 rounded-lg flex items-center justify-center p-1 ${hasPendingIssue ? 'bg-pink-100' : 'bg-muted/30'}`}>
                        <img src={assignment.tool.photo} alt={assignment.tool.name} className="max-w-full max-h-full object-contain" />
                      </div>
                    ) : (
                      <div className={`w-20 h-20 rounded-lg flex items-center justify-center ${hasPendingIssue ? 'bg-pink-100' : 'bg-muted'}`}>
                        <Package className={`w-8 h-8 ${hasPendingIssue ? 'text-pink-400' : 'text-muted-foreground'}`} />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-lg font-heading flex items-center gap-2">
                        {assignment.tool?.name}
                        <Badge variant="secondary">x{assignment.quantity}</Badge>
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">{assignment.tool?.type}</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    {hasPendingIssue && (
                      <Badge variant="outline" className="bg-pink-100 text-pink-700 border-pink-300">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Signalé
                      </Badge>
                    )}
                    {isExpiring && <Badge variant="destructive">Expire bientôt</Badge>}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {hasPendingIssue && (
                  <div className="p-2 bg-pink-100 rounded-lg text-pink-700 text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span>En cours de traitement par l'administrateur</span>
                  </div>
                )}
                <div className="text-sm space-y-2">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span className="font-medium">N° Article:</span>
                    <span className="font-mono">{assignment.tool?.article_number || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Affecté le:</span>
                    <span>{formatDate(assignment.assigned_date)}</span>
                  </div>
                  {assignment.end_date && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Fin prévue:</span>
                      <span>{formatDate(assignment.end_date)}</span>
                      {daysRemaining !== null && (
                        <Badge variant="outline" className="ml-auto">
                          {daysRemaining > 0 ? `${daysRemaining}j restants` : 'Expiré'}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                {isAdmin && !hasPendingIssue && (
                  <div className="flex gap-2">
                    <Button data-testid={`return-tool-${assignment.id}`} onClick={() => onReturn(assignment.id)} variant="outline" size="sm" className="flex-1 rounded-full">
                      <RotateCcw className="w-4 h-4 mr-2" /> Retourner
                    </Button>
                    <Button data-testid={`replace-tool-${assignment.id}`} onClick={() => onReplace(assignment)} variant="outline" size="sm" className="flex-1 rounded-full">
                      <RefreshCcw className="w-4 h-4 mr-2" /> Remplacer
                    </Button>
                  </div>
                )}
                {isAdmin && hasPendingIssue && (
                  <div className="text-center text-sm text-pink-600">
                    Traitement en attente dans la section "Signalements"
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    ) : (
      <Card className="border-2 border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Wrench className="w-12 h-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Aucun outil actuellement affecté</p>
        </CardContent>
      </Card>
    )}
  </div>
);
